import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    inject,
    signal,
    type OnDestroy,
    type OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';

import { iif, map, mergeMap, Subject, takeUntil, type Observable } from 'rxjs';

import { PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CookieService } from 'ngx-cookie-service';

import { type EduCourse, type EduCourseTariff, type EduCourseWithTariffs } from '@common/types/edu-course.type';
import { ELanguages, type EPaymentType } from '@common/enums';
import { type LoginEduEnabledForm } from '@common/enums/login-edu-enabled-form.enum';
import { TranslationLoaderService } from '@common/services/translation-loader.service';

import { AppFacade } from '@web/store/app/app.facade';
import { EduCoursesCollectionFacade } from '@web/store/collections/edu-courses/edu-courses.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { convertStringToPaymentType } from '@common/helpers/payment';

export interface CourseTariffSelection {
    course: EduCourse;
    tariffId?: number;
    paidCourseInfo?: {
        tariffName: string;
        isPaid: boolean;
        eduPaymentId: number;
        paymentMethodId: string;
        paymentType: EPaymentType;
        paymentName: string;
        price: number;
        currency: string;
    };
}

const knowledgeBaseLinkLangs = {
    [ELanguages.EN]: 'https://sendpulse.com/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.RU]: 'https://sendpulse.com/ru/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.UA]: 'https://sendpulse.com/ua/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.PT_BR]: 'https://sendpulse.com/br/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.ES_MX]: 'https://sendpulse.com/latam/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.FR]: 'https://sendpulse.com/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.TR]: 'https://sendpulse.com/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.IT]: 'https://sendpulse.com/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.ID]: 'https://sendpulse.com/knowledge-base/edu/settings/manage-pricing-plans',
    [ELanguages.FIL]: 'https://sendpulse.com/knowledge-base/edu/settings/manage-pricing-plans',
};

type PaidType = 'paid' | 'free' | 'all';

@Component({
    selector: 'web-course-tariff-selector',
    templateUrl: './course-tariff-selector.component.html',
    styleUrls: ['./course-tariff-selector.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        // For `ng-select` alone — it is a ControlValueAccessor and has no other binding.
        FormsModule,
        TranslateModule,
        LetDirective,
        NgSelectModule,
        PopoverModule,
        TooltipModule,
    ],
})
export class CourseTariffSelectorComponent implements OnInit, OnDestroy {
    private readonly eduCourseFacade: EduCoursesCollectionFacade = inject(EduCoursesCollectionFacade);
    private readonly routerFacade: RouterFacade = inject(RouterFacade);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly cookieService = inject(CookieService);

    @Input() selectedCourse: number;
    @Input() selectedTariffId: number;
    @Input() isDisabled = false;
    @Input() showError = false;
    /**
     * @description
     * If true, the component will show only paid tariffs
     *
     * @type {boolean}
     * @memberof CourseTariffSelectorComponent
     */
    @Input() paid: PaidType = 'free';
    /**
     * @description
     * Only for login/reg form widgets.
     *
     * @type {LoginEduEnabledForm}
     * @memberof CourseTariffSelectorComponent
     */
    @Input() loginEduEnabledForm!: LoginEduEnabledForm;

    @Input() tooltipText?: string;

    @Output() selectionChange = new EventEmitter<CourseTariffSelection>();

    public eduCourses$: Observable<EduCourseWithTariffs[]>;

    public readonly courseId = signal<number | null>(null);
    public readonly tariffId = signal<number | null>(null);
    public knowledgeBaseLink: string =
        knowledgeBaseLinkLangs[TranslationLoaderService.getCurrentLocale(this.cookieService)];

    private readonly destroyed$ = new Subject<void>();

    public ngOnInit(): void {
        this.eduCourses$ = this.appFacade.isAdminCollectionWrapper$(this.getEduCourses$());

        if (this.selectedCourse) {
            this.courseId.set(this.selectedCourse);
        }
        if (this.selectedTariffId) {
            this.tariffId.set(this.selectedTariffId);
        }
    }

    public changeEduCourse(event: Event, courses: EduCourseWithTariffs[]): void {
        const courseId = Number((event.target as HTMLSelectElement).value);
        const course = courses.find((item) => item.id === courseId);

        if (course) {
            this.courseId.set(course.id);
            this.tariffId.set(null);
            this.selectionChange.emit({ course });
        }
    }

    public changeEduCourseTariff(tariffId: number, courses: EduCourseWithTariffs[]): void {
        let tariff: EduCourseTariff;
        const course = courses.find((item) =>
            item.tariffs.some((t) => {
                const match = t.id === tariffId;
                if (match) {
                    tariff = t;
                }
                return match;
            })
        );

        if (course) {
            this.courseId.set(course.id);
            this.tariffId.set(tariffId);
            if (tariff.paymentMethods[0]) {
                const paymentMethod = tariff.paymentMethods[0];
                this.selectionChange.emit({
                    course,
                    tariffId,
                    paidCourseInfo: {
                        tariffName: tariff.name,
                        isPaid: true,
                        eduPaymentId: paymentMethod.id,
                        price: paymentMethod.price,
                        paymentName: paymentMethod.name,
                        paymentMethodId: paymentMethod.paymentId,
                        paymentType: convertStringToPaymentType(paymentMethod.paymentType),
                        currency: paymentMethod.currency,
                    },
                });
                return;
            }
            this.selectionChange.emit({ course, tariffId });
        }
    }

    public resetSelection(): void {
        this.courseId.set(null);
        this.tariffId.set(null);
        this.selectionChange.emit({ course: null, tariffId: null });
    }

    public getEduCourses$(): Observable<EduCourseWithTariffs[]> {
        return this.routerFacade.routerParams$.pipe(
            mergeMap((params) =>
                iif(
                    () => this._paid,
                    // Paid mode (payment form, login/reg form) is deliberately NOT
                    // scoped to the current site: a paid course can be sold from any
                    // page, and the payment widget's own data layer reads the whole
                    // course list (`eduCoursesSortByPaid$`). Filtering the dropdown by
                    // siteId here desynced the two — the auto-picked course was missing
                    // from the options, so ng-select rendered an empty label, and on a
                    // non-edu site the list was empty ("no active courses").
                    this.eduCourseFacade.eduCoursesSortByPaid$,
                    this.eduCourseFacade.eduCoursesWithTariffsBySiteId$(params.siteId)
                )
            ),
            map((courses) => {
                return courses.map((item) => ({
                    ...item,
                    tariffs: (item.tariffs ?? []).filter((tariff) => {
                        if (this.paid === 'free') {
                            return !tariff.isPaid;
                        }

                        if (this.paid === 'paid') {
                            return tariff.isPaid;
                        }

                        return true;
                    }),
                }));
            }),
            takeUntil(this.destroyed$)
        );
    }

    private get _paid(): boolean {
        return this.paid === 'paid' || this.paid === 'all';
    }

    public getCurrencyLabel(item: EduCourseTariff) {
        if (!item.paymentMethods?.length) {
            return null;
        }

        const currency = item.paymentMethods[0].currency;
        const price = item.paymentMethods[0].price;

        return ` - ${price} ${currency}`;
    }

    public ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
